// ============================================
// APPLICATION - Encuesta Hooks
// ============================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { encuestaRepository } from '../../infrastructure/http/encuesta.repository';
import { useAuthStore } from '../../state/auth.store';
import type { Encuesta } from '../../domain/entities';

interface SubmitEncuestaParams {
  encuestaId: number;
  personaContesta: string;
  respuestas: Array<{
    encuestaPreguntaId: number;
    respuesta: string;
  }>;
}

/**
 * Hook para obtener una encuesta
 */
export const useEncuesta = (encuestaId: string | undefined) => {
  return useQuery<Encuesta>({
    queryKey: ['encuesta', encuestaId],
    queryFn: () => encuestaRepository.getEncuesta(encuestaId!),
    enabled: !!encuestaId,
    staleTime: 10 * 60 * 1000, // 10 minutos
  });
};

/**
 * Hook para enviar respuestas de encuesta
 */
export const useSubmitEncuesta = () => {
  const queryClient = useQueryClient();
  const setSession = useAuthStore((state) => state.setSession);
  const session = useAuthStore((state) => state.session);

  return useMutation({
    mutationFn: (params: SubmitEncuestaParams) =>
      encuestaRepository.submitRespuestas(params),
    onSuccess: async () => {
      console.log('[useSubmitEncuesta] Encuesta enviada exitosamente');
      
      if (!session) {
        console.warn('[useSubmitEncuesta] No hay sesión en el store');
        return;
      }
      
      // 1. Crear sesión actualizada con encuesta completada
      const updatedSession = {
        ...session,
        mustAnswerSurvey: false,
        encuestaId: null,
      };
      
      console.log('[useSubmitEncuesta] Actualizando sesión:', {
        antes: { mustAnswerSurvey: session.mustAnswerSurvey, encuestaId: session.encuestaId },
        despues: { mustAnswerSurvey: updatedSession.mustAnswerSurvey, encuestaId: updatedSession.encuestaId }
      });
      
      // 2. Actualizar en el auth store
      setSession(updatedSession);
      
      // 3. Actualizar DIRECTAMENTE la query cache (crítico para evitar loops)
      queryClient.setQueryData(['session'], updatedSession);
      
      console.log('[useSubmitEncuesta] Sesión actualizada en store y query cache');
      
      // 4. Refetch en background para confirmar con el servidor (no bloquea la navegación)
      queryClient.refetchQueries({ queryKey: ['session'] }).catch((error) => {
        console.error('[useSubmitEncuesta] Error en refetch (no crítico):', error);
      });
    },
  });
};
